const BRANDFETCH_CLIENT_ID = '1idSjBHt_B8aw_qWjnC';
const BRANDFETCH_BASE_URL = 'https://cdn.brandfetch.io';
const DEFAULT_HEIGHT = 128;

// Cache for avatar URLs to avoid recomputation
const avatarUrlCache = new Map<string, string | null>();

type BrandfetchType = 'icon' | 'logo' | 'symbol';
type BrandfetchTheme = 'light' | 'dark';
type BrandfetchFallback = 'brandfetch' | 'transparent' | 'lettermark' | '404';

export interface BrandfetchOptions {
  type?: BrandfetchType;
  theme?: BrandfetchTheme;
  fallback?: BrandfetchFallback;
  height?: number;
}

const DEFAULT_OPTIONS: Required<Pick<BrandfetchOptions, 'type' | 'fallback'>> & Partial<BrandfetchOptions> = {
  type: 'icon',
  fallback: 'lettermark',
  height: DEFAULT_HEIGHT,
};

function sanitizeDomain(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }
  const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
  const withoutUser = withoutProtocol.split('@').pop() ?? '';
  const candidate = withoutUser.split('/')[0]?.replace(/^\.+|\.+$/g, '');
  if (!candidate || candidate.includes(' ') || !candidate.includes('.')) {
    return null;
  }
  return candidate;
}

export function getDomainFromEmail(email?: string | null): string | null {
  return sanitizeDomain(email);
}

function sanitizeHeight(value?: number): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  const rounded = Math.round(value);
  if (rounded <= 0) {
    return null;
  }
  return Math.min(Math.max(rounded, 32), 512);
}

export function getBrandfetchLogoUrl(input?: string | null, options?: BrandfetchOptions): string | null {
  const domain = sanitizeDomain(input);
  if (!domain) {
    return null;
  }

  // Create cache key from domain and options
  const cacheKey = `${domain}:${JSON.stringify(options || {})}`;

  // Check cache first
  if (avatarUrlCache.has(cacheKey)) {
    return avatarUrlCache.get(cacheKey) ?? null;
  }

  const merged: BrandfetchOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const encodedDomain = encodeURIComponent(domain);
  const pathSegments: string[] = [encodedDomain, merged.type ?? DEFAULT_OPTIONS.type];

  if (merged.theme) {
    pathSegments.push('theme', merged.theme);
  }

  if (merged.fallback ?? DEFAULT_OPTIONS.fallback) {
    pathSegments.push('fallback', merged.fallback ?? DEFAULT_OPTIONS.fallback);
  }

  const height = sanitizeHeight(merged.height ?? DEFAULT_OPTIONS.height);
  if (height) {
    pathSegments.push('h', String(height));
  }

  const params = new URLSearchParams({
    c: BRANDFETCH_CLIENT_ID,
  });

  const url = `${BRANDFETCH_BASE_URL}/${pathSegments.join('/')}` + `?${params.toString()}`;

  // Cache the result
  avatarUrlCache.set(cacheKey, url);

  return url;
}

interface AvatarSource {
  email?: string | null;
  domain?: string | null;
  avatarUrl?: string | null;
}

export function resolveAvatarUrl({ email, domain, avatarUrl }: AvatarSource, options?: BrandfetchOptions): string | null {
  if (avatarUrl && avatarUrl.trim()) {
    return avatarUrl;
  }
  const candidateDomain = domain ?? getDomainFromEmail(email ?? undefined);
  if (candidateDomain) {
    return getBrandfetchLogoUrl(candidateDomain, options);
  }
  return null;
}
